import { useState, useRef, useEffect, useMemo } from 'react';
import { Upload } from 'lucide-react';
import { TEMPLATE_BOUNDS, TEMPLATE_SIZE, LOGO_BOUNDS, DEFAULT_LOGO_SIZE, getAdaptiveFontSize, scaleBounds, wrapTextLines } from '../../../shared/lib/video';
import { ensureCanvasFont } from '../../../shared/lib/fontLoader';
import { usePinchFontSize } from '../../../shared/lib/usePinchFontSize';
import { buildFontVariationCSS } from '../../../shared/lib/variableFontUtils';
import { drawOverlay } from '../../../shared/lib/webcodecs/overlay-utils';
import { buildReelOverlayParams } from '../lib/buildReelOverlayParams';

interface ReelPreviewProps {
  text: string;
  videoFile?: File | null;
  logoFile?: File | null;
  onTextPositionChange?: (position: { x: number; y: number; previewWidth: number; previewHeight: number; textBoxWidth: number; bottom: number; lines: string[]; fontSize: number; centerXFrac: number; fromDrag: boolean }) => void;
  onTextChange?: (text: string) => void;
  onVideoUploadClick?: () => void;
  onLogoUploadClick?: () => void;
  // Template props
  backgroundColor?: string;
  backgroundAlpha?: number;
  textShadowEnabled?: boolean;
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowWidth?: number;
  textSoftShadowEnabled?: boolean;
  textSoftShadowColor?: string;
  textSoftShadowBlur?: number;
  textSoftShadowOffsetX?: number;
  textSoftShadowOffsetY?: number;
  textSoftShadowOpacity?: number;
  textShadowOpacity?: number;
  textColor?: string;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  fontFamily?: string;
  fontUrl?: string;
  textUppercase?: boolean;
  fontVariationSettings?: Record<string, number>;
  logoPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  textDuration?: number | 'full';
  fontSize?: number | null;
  onFontSizeChange?: (size: number) => void;
  logoSize?: number;
  textLineSpacing?: number;
  /** Превью стиля без видео: рендерит оверлей (текст/лого) поверх плейсхолдер-фона. */
  previewOnly?: boolean;
  /** URL логотипа (сохранённый шаблон) — фоллбэк, когда logoFile не задан. */
  logoSrc?: string;
  /** URL PNG-каркаса 1080×1920 (рисуется поверх видео). */
  frameSrc?: string;
  /** URL фона рилса 1080×1920 (рисуется позади видео, виден в полях letterbox). */
  reelBackgroundUrl?: string;
  /** Потолок авто-кегля, template px @1080 (см. Template.autoFontMax). */
  autoFontMax?: number;
  /** Разрыв строки после крашеной части (см. Template.colonBreak). */
  colonBreak?: boolean;
  /** Рабочая ширина текстовой зоны, px @1080 (см. Template.textZoneWidth). */
  textZoneWidth?: number;
  textAlign?: 'left' | 'center' | 'right';
  textBoxStyle?: 'rounded' | 'square' | 'square-flush' | 'square-flush-right';
  textBoxAngle?: number;
  textBoxRadius?: number;
  textBoxPadding?: number;
  textBoxPaddingTop?: number;
  textBoxPaddingLeft?: number;
  textBoxPaddingRight?: number;
  /** Режим «менять размер пальцами»: рамка + pinch включён. */
  resizeActive?: boolean;
  /** Тоггл режима размера из оверлея превью (подсказка/ОК). */
  onResizeActiveChange?: (active: boolean) => void;
  /** Stored reel box center X (fraction 0..1). When set, the preview renders
   *  center-anchored and the hit-box is positioned by its center. */
  reelTextCenterX?: number;
  /** Stored bottom anchor (template px 0..1920) to seed the initial position. */
  reelTextBottom?: number;
}

export function ReelPreview({ text, videoFile, logoFile, onTextPositionChange, onTextChange, onVideoUploadClick, onLogoUploadClick, backgroundColor, backgroundAlpha, textShadowEnabled, textShadowColor, textShadowBlur, textShadowWidth, textSoftShadowEnabled, textSoftShadowColor, textSoftShadowBlur, textSoftShadowOffsetX, textSoftShadowOffsetY, textSoftShadowOpacity, textShadowOpacity, textColor, useTwoColors, textColorSecondary, fontFamily, fontUrl, textUppercase, fontVariationSettings, logoPosition, textDuration = 'full', fontSize: fontSizeProp = null, onFontSizeChange, logoSize, textLineSpacing, previewOnly = false, logoSrc, frameSrc, reelBackgroundUrl, textAlign, textBoxStyle, textBoxAngle, textBoxRadius, textBoxPadding, textBoxPaddingTop, textBoxPaddingLeft, textBoxPaddingRight, resizeActive = false, onResizeActiveChange, reelTextCenterX, reelTextBottom, autoFontMax, colonBreak, textZoneWidth }: ReelPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const logoBitmapRef = useRef<{ url: string; bmp: ImageBitmap | null }>({ url: '', bmp: null });
  const frameBitmapRef = useRef<{ url: string; bmp: ImageBitmap | null }>({ url: '', bmp: null });
  const textBoxRef = useRef<HTMLDivElement>(null);
  const textContentRef = useRef<HTMLDivElement>(null);
  const [videoUrl, setVideoUrl] = useState<string>('')
  const [logoUrl, setLogoUrl] = useState<string>('');
  // x-сид = левый край безопасной зоны: тот же, куда drawOverlay клампит бокс.
  const [textPosition, setTextPosition] = useState(() => ({ x: TEMPLATE_BOUNDS.left, bottom: reelTextBottom ?? 380 }));
  const [isDragging, setIsDragging] = useState(false);
  // Синхронизируем bottom со stored-значением шаблона. Реагируем ТОЛЬКО на смену
  // самого prop (приходит новое сохранённое значение), а не на каждый рендер.
  //
  // Почему именно так:
  // - TemplatePage гидрирует reelTextBottom через useEffect (позже первого рендера),
  //   поэтому «seed один раз» защёлкивал дефолт 380 до прихода сохранённого значения
  //   и позиция терялась при переоткрытии шаблона.
  // - В форме публикации reelTextBottom = стабильное значение шаблона и НЕ обновляется
  //   при drag (onTextPositionChange туда не пишет). Если синкать при isDragging→false,
  //   перетаскивание сбрасывается обратно на шаблонное. Поэтому завязка на смену prop:
  //   пока prop не меняется — drag живёт; меняется (другой шаблон/гидрация) — пере-сидим.
  const lastSeededBottomRef = useRef<number | null>(reelTextBottom ?? null);
  useEffect(() => {
    if (reelTextBottom == null || lastSeededBottomRef.current === reelTextBottom) return;
    lastSeededBottomRef.current = reelTextBottom;
    setTextPosition((pos) => ({ ...pos, bottom: reelTextBottom }));
  }, [reelTextBottom]);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isEditingText, setIsEditingText] = useState(false);
  const [fontLoaded, setFontLoaded] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartY, setResizeStartY] = useState<number | null>(null);
  const [resizeStartSize, setResizeStartSize] = useState<number | null>(null);

  const [containerScale, setContainerScale] = useState(1);

  const wrapResult = useMemo(() => {
    // Пустой текст: рисовать нечего, число нужно только как начальная точка
    // для щипка и подписи.
    if (!text) return { lines: [] as string[], fontSize: fontSizeProp ?? getAdaptiveFontSize(0), textBoxWidth: 0, primaryLines: 1, splitChars: 0 };
    return wrapTextLines({
      text,
      fontSize: fontSizeProp,
      fontFamily: fontFamily || 'Arial',
      fontVariationSettings,
      uppercase: !!textUppercase,
      bottom: textPosition.bottom,
      previewWidth: containerRef.current?.getBoundingClientRect().width ?? TEMPLATE_SIZE.width,
      lineSpacing: textLineSpacing,
      // Те же поля отступа, что уходят в drawOverlay ниже по этому же файлу.
      textBoxPadding, textBoxPaddingTop, textBoxPaddingLeft, textBoxPaddingRight,
      maxFontSize: autoFontMax,
      colonBreak,
      textZoneWidth,
      plateVisible: (backgroundAlpha ?? 1) > 0.05,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // fontLoaded обязателен в списке: wrapTextLines меряет строки своим канвасом,
    // и пока кастомный шрифт не загружен, measureText отдаёт метрики системного
    // фолбэка. Он шире — перенос ломает строки раньше, чем нужно, а рисует их
    // канвас ниже уже настоящим шрифтом (он ждёт fontLoaded). Строки выходили
    // короче лимита, и справа от плашки оставалась пустая четверть зоны —
    // тем заметнее, чем у́же шрифт паблика (отзыв 04.09, zhuldyzdy_news).
  }, [text, fontSizeProp, fontFamily, fontLoaded, fontVariationSettings, textUppercase, textPosition.bottom, containerScale, textLineSpacing,
      textBoxPadding, textBoxPaddingTop, textBoxPaddingLeft, textBoxPaddingRight, autoFontMax, colonBreak, textZoneWidth, backgroundAlpha]);

  /**
   * Размер, который реально уйдёт в кадр: ручной, иначе подобранный при
   * переносе строк. Раньше тут стоял getAdaptiveFontSize по длине текста —
   * он не знает ни про плашку, ни про шрифт и отдаёт 16–24 px, тогда как
   * подбор садится примерно на 36. Из-за этого расходились с картинкой сразу
   * три вещи: подпись под превью, стартовая точка растягивания и невидимое
   * поле ввода с кареткой, в котором правят текст двойным щелчком.
   */
  const fontSize = wrapResult.fontSize;

  // Pinch двумя пальцами в превью → меняем размер текста (мобилка).
  usePinchFontSize(containerRef, {
    value: fontSize,
    min: 12,
    max: 72,
    onChange: onFontSizeChange,
    enabled: !!onFontSizeChange && resizeActive,
  });

  const renderLines = wrapResult.lines;

  // 1:1 рендер оверлея той же drawOverlay что и серверный бёрн (тот же код, тот же Canvas2D).
  // Видимый текст/лого рисует этот canvas; DOM-бокс ниже — прозрачный hit-box для drag/resize/edit.
  useEffect(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas || !fontLoaded) return;
    canvas.width = TEMPLATE_SIZE.width;
    canvas.height = TEMPLATE_SIZE.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let cancelled = false;

    // Загрузка ImageBitmap по url с кэшем в ref.
    const loadBmp = (url: string, ref: { current: { url: string; bmp: ImageBitmap | null } }) =>
      new Promise<ImageBitmap | null>((resolve) => {
        if (!url) { ref.current = { url: '', bmp: null }; resolve(null); return; }
        if (ref.current.url === url && ref.current.bmp) { resolve(ref.current.bmp); return; }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => createImageBitmap(img)
          .then((bmp) => { ref.current = { url, bmp }; resolve(bmp); })
          .catch(() => resolve(null));
        img.onerror = () => resolve(null);
        img.src = url;
      });

    (async () => {
      const [logoBmp, frameBmp] = await Promise.all([
        loadBmp(logoUrl || logoSrc || '', logoBitmapRef),
        loadBmp(frameSrc || '', frameBitmapRef),
      ]);
      if (cancelled) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const overlay = buildReelOverlayParams({
        text, lines: renderLines, primaryLines: wrapResult.primaryLines,
        splitChars: wrapResult.splitChars, fontSize: wrapResult.fontSize,
        posX: textPosition.x, posBottom: textPosition.bottom, containerScale,
        fontFamily, fontUrl, textUppercase, fontVariationSettings,
        backgroundColor, backgroundAlpha, textColor, useTwoColors, textColorSecondary,
        textShadowEnabled, textShadowColor, textShadowBlur, textShadowWidth,
        textSoftShadowEnabled, textSoftShadowColor, textSoftShadowBlur,
        textSoftShadowOffsetX, textSoftShadowOffsetY, textSoftShadowOpacity, textShadowOpacity,
        logoPosition, logoSize, lineSpacing: textLineSpacing, textAlign,
        textBoxStyle, textBoxAngle, textBoxRadius, textBoxPadding, textBoxPaddingTop, textBoxPaddingLeft, textBoxPaddingRight,
        hCenterX: isDragging
          ? (textPosition.x + wrapResult.textBoxWidth / 2) / TEMPLATE_SIZE.width
          : (reelTextCenterX ?? 0),
      });
      // drawOverlay типизирован под OffscreenCanvas, но Canvas2D API совместим.
      drawOverlay(ctx as any, canvas as any, 0, overlay, logoBmp, frameBmp);
    })();

    return () => { cancelled = true; };
  }, [
    text, renderLines, wrapResult.fontSize, textPosition.x, textPosition.bottom, containerScale,
    fontFamily, fontUrl, fontLoaded, textUppercase, fontVariationSettings,
    backgroundColor, backgroundAlpha, textColor, useTwoColors, textColorSecondary,
    textShadowEnabled, textShadowColor, textShadowBlur, textShadowWidth,
    textSoftShadowEnabled, textSoftShadowColor, textSoftShadowBlur,
    textSoftShadowOffsetX, textSoftShadowOffsetY, textSoftShadowOpacity, textShadowOpacity,
    logoPosition, logoSize, textLineSpacing, logoUrl, logoSrc, frameSrc, textAlign,
    textBoxStyle, textBoxAngle, textBoxRadius, textBoxPadding, textBoxPaddingTop, textBoxPaddingLeft, textBoxPaddingRight,
    reelTextCenterX, isDragging, wrapResult.textBoxWidth,
  ]);

  // Вычисляем maxWidth динамически на основе текущей позиции
  const getMaxWidth = () => {
    if (!textBoxRef.current) return TEMPLATE_BOUNDS.right - TEMPLATE_BOUNDS.left;
    
    const textBoxHeight = textBoxRef.current.offsetHeight / containerScale;
    const textBoxBottomY = TEMPLATE_SIZE.height - textPosition.bottom;
    const textBoxTopY = textBoxBottomY - textBoxHeight;
    
    // Если верх блока выше линии выреза (1150px) - полная ширина
    if (textBoxTopY < TEMPLATE_BOUNDS.rightCornerTop) {
      return TEMPLATE_BOUNDS.right - TEMPLATE_BOUNDS.left;
    }
    
    // Если верх блока в зоне выреза или ниже - уменьшенная ширина
    return TEMPLATE_BOUNDS.rightCornerLeft - TEMPLATE_BOUNDS.left;
  };

  // Вычисляем масштаб контейнера
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateScale = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setContainerScale(rect.width / TEMPLATE_SIZE.width);
    };
    
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // Отправляем позицию текста
  useEffect(() => {
    if (!containerRef.current || !onTextPositionChange || !textBoxRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const textBoxHeight = textBoxRef.current.offsetHeight;
    const textBoxWidth = textBoxRef.current.offsetWidth;
    
    if (textBoxHeight === 0) return;
    
    const scale = rect.height / TEMPLATE_SIZE.height;
    const bottomInPreview = textPosition.bottom * scale;
    const xInPreview = textPosition.x * scale;
    const y = rect.height - bottomInPreview - textBoxHeight;
    
    onTextPositionChange({
      x: xInPreview,
      y: y,
      previewWidth: rect.width,
      previewHeight: rect.height,
      textBoxWidth: textBoxWidth,
      bottom: textPosition.bottom,
      lines: wrapResult.lines,
      fontSize: wrapResult.fontSize,
      centerXFrac: (textPosition.x + wrapResult.textBoxWidth / 2) / TEMPLATE_SIZE.width,
      fromDrag: false,
    });
  }, [containerScale, textPosition.bottom, textPosition.x, text, videoFile, wrapResult]);

  // Создаем URL для видео
  useEffect(() => {
    if (videoFile) {
      const url = URL.createObjectURL(videoFile);
      setVideoUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setVideoUrl('');
    }
  }, [videoFile]);

  // Создаем URL для логотипа
  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setLogoUrl('');
    }
  }, [logoFile]);

  // Загружаем + прогреваем шрифт (для canvas, вкл. iOS WKWebView).
  useEffect(() => {
    let cancelled = false;
    setFontLoaded(false);
    ensureCanvasFont(fontFamily || 'Arial', fontUrl).then(() => { if (!cancelled) setFontLoaded(true); });
    return () => { cancelled = true; };
  }, [fontFamily, fontUrl]);

  const handleMouseDown = (e: React.PointerEvent) => {
    if (!textBoxRef.current || isEditingText) return;

    e.currentTarget.setPointerCapture?.(e.pointerId);
    setIsDragging(true);
    const rect = textBoxRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeStartY(e.clientY);
    setResizeStartSize(fontSize);
  };

  const handleTextDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingText(true);
    
    // Фокус на contenteditable
    if (textContentRef.current) {
      textContentRef.current.focus();
      
      // Выделяем весь текст
      const range = document.createRange();
      range.selectNodeContents(textContentRef.current);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  const handleTextInput = (e: React.FormEvent<HTMLDivElement>) => {
    if (!onTextChange) return;
    const newText = e.currentTarget.textContent || '';
    onTextChange(newText);
  };

  const handleTextBlur = () => {
    setIsEditingText(false);
  };

  const handleTextKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    
    // Предотвращаем перенос строки через Enter (если нужен перенос - используем Shift+Enter)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      textContentRef.current?.blur();
    }
  };

  const handleBackgroundClick = () => {
    if (!videoFile && onVideoUploadClick) {
      onVideoUploadClick();
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onLogoUploadClick) {
      onLogoUploadClick();
    }
  };

  // Обработка перетаскивания с RAF оптимизацией
  useEffect(() => {
    let rafId: number | null = null;
    let lastMouseEvent: PointerEvent | null = null;

    const handleMouseMove = (e: PointerEvent) => {
      // Сохраняем последнее событие мыши
      lastMouseEvent = e;

      // Если уже запланирован RAF, ничего не делаем
      if (rafId !== null) return;

      // Планируем обновление на следующий кадр (60 FPS макс)
      rafId = requestAnimationFrame(() => {
        if (!lastMouseEvent || !isDragging || !containerRef.current || !textBoxRef.current) {
          rafId = null;
          return;
        }

        const e = lastMouseEvent;
        const containerRect = containerRef.current.getBoundingClientRect();
        const textBoxHeight = textBoxRef.current.offsetHeight;
        const textBoxWidth = textBoxRef.current.offsetWidth;

        let newXScaled = e.clientX - containerRect.left - dragOffset.x;
        let newYScaled = e.clientY - containerRect.top - dragOffset.y;

        const scale = containerRect.width / TEMPLATE_SIZE.width;
        const scaledBounds = scaleBounds(TEMPLATE_BOUNDS, scale);
        
        const textBoxBottom = newYScaled + textBoxHeight;
        const textBoxRight = newXScaled + textBoxWidth;
        
        // Определяем границы в зависимости от позиции
        let maxRight = scaledBounds.right;
        let maxBottom = scaledBounds.bottomLeftTop;
        
        if (textBoxBottom <= scaledBounds.rightCornerTop) {
          maxRight = scaledBounds.right;
          maxBottom = scaledBounds.rightCornerTop;
        } else if (newYScaled >= scaledBounds.rightCornerTop && textBoxBottom <= scaledBounds.bottomLeftTop) {
          maxRight = scaledBounds.rightCornerLeft;
          maxBottom = scaledBounds.bottomLeftTop;
        } else if (newYScaled >= scaledBounds.bottomLeftTop) {
          newYScaled = Math.min(newYScaled, scaledBounds.bottomLeftTop - textBoxHeight);
          maxRight = scaledBounds.rightCornerLeft;
          maxBottom = scaledBounds.bottomLeftTop;
        }
        
        if (textBoxBottom > scaledBounds.rightCornerTop && newYScaled < scaledBounds.rightCornerBottom) {
          maxRight = Math.min(maxRight, scaledBounds.rightCornerLeft);
        }
        
        newXScaled = Math.max(scaledBounds.left, Math.min(newXScaled, maxRight - textBoxWidth));
        newYScaled = Math.max(scaledBounds.top, Math.min(newYScaled, maxBottom - textBoxHeight));

        const newX = newXScaled / scale;
        const newY = newYScaled / scale;
        const newBottom = TEMPLATE_SIZE.height - newY - (textBoxHeight / scale);

        setTextPosition({ x: newX, bottom: newBottom });

        const centerXFrac = Math.min(1, Math.max(0, (newX + wrapResult.textBoxWidth / 2) / TEMPLATE_SIZE.width));

        if (onTextPositionChange && textBoxRef.current) {
          onTextPositionChange({
            x: newX,
            y: newY,
            previewWidth: containerRect.width,
            previewHeight: containerRect.height,
            textBoxWidth: textBoxRef.current.offsetWidth,
            bottom: newBottom,
            lines: wrapResult.lines,
            fontSize: wrapResult.fontSize,
            centerXFrac,
            fromDrag: true,
          });
        }

        // Сбрасываем состояние
        rafId = null;
        lastMouseEvent = null;
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      
      // Отменяем pending RAF если есть
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      lastMouseEvent = null;
    };

    if (isDragging) {
      document.addEventListener('pointermove', handleMouseMove);
      document.addEventListener('pointerup', handleMouseUp);
      document.addEventListener('pointercancel', handleMouseUp);
    }

    return () => {
      document.removeEventListener('pointermove', handleMouseMove);
      document.removeEventListener('pointerup', handleMouseUp);
      document.removeEventListener('pointercancel', handleMouseUp);

      // Cleanup RAF при unmount
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isDragging, dragOffset, onTextPositionChange, containerScale, textPosition.bottom]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (resizeStartY === null || resizeStartSize === null) return;
      const delta = e.clientY - resizeStartY;
      const newSize = Math.min(72, Math.max(12, Math.round(resizeStartSize + delta / 4)));
      onFontSizeChange?.(newSize);
    };

    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStartY, resizeStartSize, onFontSizeChange]);

  const maxWidth = getMaxWidth();
  // На белом превью-фоне белые направляющие не видны — делаем тёмными.
  const guideStroke = previewOnly ? '#94a3b8' : '#FFFFFF';

  return (
    <div
      ref={containerRef}
      className={`relative w-full aspect-[9/16] rounded-lg overflow-hidden border ${previewOnly ? 'bg-background' : 'bg-gray-900'} ${resizeActive ? 'ring-2 ring-primary ring-offset-2' : ''}`}
      style={resizeActive ? { touchAction: 'none' } : undefined}
    >
      {/* Подсказка у текста (неактивно) → включить режим размера. */}
      {!resizeActive && onResizeActiveChange && !!text && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onResizeActiveChange(true); }}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 rounded-full bg-black/70 px-3 py-1.5 text-xs font-medium text-white shadow backdrop-blur-sm"
        >
          👆 Нажми, чтобы изменить размер
        </button>
      )}
      {resizeActive && (
        <>
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow pointer-events-none">
            ✌️ Двумя пальцами — размер · {Math.round(fontSize)}px
          </div>
          {onResizeActiveChange && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onResizeActiveChange(false); }}
              className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 rounded-full bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground shadow-lg"
            >
              ОК
            </button>
          )}
        </>
      )}
      {/* Background video or image */}
      <div 
        className={`absolute inset-0 ${!videoUrl && 'cursor-pointer hover:opacity-90 transition-opacity'}`}
        onClick={handleBackgroundClick}
      >
        {videoUrl ? (
          <>
            {/* Фон рилса позади видео — виден в полях letterbox при object-contain. */}
            {reelBackgroundUrl && (
              <img
                src={reelBackgroundUrl}
                alt=""
                className="absolute inset-0 size-full object-fill pointer-events-none"
              />
            )}
            {/* Фона нет — в полях горизонтального видео показываем размытый
                cover-слой того же видео, как рисует паблиш, а не чёрные полосы. */}
            {!reelBackgroundUrl && (
              <>
                <video
                  className="absolute inset-0 max-w-none object-cover pointer-events-none size-full blur-2xl scale-110"
                  src={videoUrl}
                  loop
                  muted
                  autoPlay
                  playsInline
                  aria-hidden
                />
                <div className="absolute inset-0 bg-black/25 pointer-events-none" />
              </>
            )}
            <video
              className="absolute inset-0 max-w-none object-contain pointer-events-none size-full"
              src={videoUrl}
              loop
              muted
              autoPlay
              playsInline
            />
          </>
        ) : (
          <div className={`absolute inset-0 flex items-center justify-center ${previewOnly ? 'bg-white' : 'bg-black'}`}>
            <div className={`text-center ${previewOnly ? 'text-gray-400' : 'text-white/60'}`}>
              <Upload className="mx-auto mb-4" size={64} strokeWidth={1.5} />
              <p className="text-lg">Загрузите видео</p>
              <p className={`text-sm mt-2 ${previewOnly ? 'text-gray-300' : 'text-white/40'}`}>Нажмите для выбора файла</p>
            </div>
          </div>
        )}
      </div>

      {/* 1:1 оверлей (текст+лого+тени) — тот же drawOverlay что серверный бёрн */}
      <canvas
        ref={overlayCanvasRef}
        className="absolute inset-0 size-full pointer-events-none"
      />

      {/* Borders overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1080 1920">
          <g id="Borders">
            {/* Внутренний контур безопасной зоны — прямоугольник от TEMPLATE_BOUNDS.
                Вырез правого уголка убран (отзыв 04.09), координаты больше не
                зашиты: пунктир двигается вместе с границами. */}
            <rect
              x={TEMPLATE_BOUNDS.left}
              y={TEMPLATE_BOUNDS.top}
              width={TEMPLATE_BOUNDS.right - TEMPLATE_BOUNDS.left}
              height={1540 - TEMPLATE_BOUNDS.top}
              stroke={guideStroke} strokeOpacity="0.5" strokeWidth="2" strokeDasharray="8 4"
            />
          </g>
        </svg>
      </div>

      {/* Logo area */}
      <div
        className={`absolute border-2 border-dashed transition-all ${(logoUrl || logoSrc) ? 'border-transparent hover:border-white/30' : 'border-white/30 hover:border-white/50'} rounded-lg cursor-pointer`}
        style={{
          left: `${LOGO_BOUNDS.x * containerScale}px`,
          top: `${LOGO_BOUNDS.y * containerScale}px`,
          width: `${(logoSize && logoSize > 0 ? logoSize : DEFAULT_LOGO_SIZE) * containerScale}px`,
          height: `${(logoSize && logoSize > 0 ? logoSize : DEFAULT_LOGO_SIZE) * containerScale}px`,
        }}
        onClick={handleLogoClick}
      >
        {(logoUrl || logoSrc) ? (
          <img
            src={logoUrl || logoSrc}
            alt="Logo"
            className="w-full h-full object-contain pointer-events-none opacity-0"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/50 text-center p-4">
            <div>
              <div className="text-xs">Нажмите для загрузки логотипа</div>
              <div className="text-[10px] mt-1">240×240px</div>
            </div>
          </div>
        )}
      </div>

      {/* Draggable text box */}
      {(videoFile || previewOnly) && text && (
        <div
          ref={textBoxRef}
          className={`group absolute inline-block p-[12px] rounded-[12px] transition-shadow hover:shadow-lg hover:ring-2 hover:ring-white/50 outline-none select-none ${isEditingText ? 'cursor-text' : 'cursor-move'}`}
          style={{
            // Текст/drag всегда на своей позиции. При square-flush canvas сам тянет
            // ТОЛЬКО подложку влево до края — текст (этот hit-box) не двигается.
            left: `${Math.max(TEMPLATE_BOUNDS.left, (reelTextCenterX ?? 0) * TEMPLATE_SIZE.width - wrapResult.textBoxWidth / 2) * containerScale}px`,
            bottom: `${textPosition.bottom * containerScale}px`,
            maxWidth: `${maxWidth * containerScale}px`,
            // Фон/текст рисует canvas-оверлей (1:1). Бокс — прозрачный hit-box.
            backgroundColor: 'transparent',
            // Pointer-drag: запрещаем браузеру скроллить/зумить страницу при drag пальцем.
            touchAction: 'none',
          }}
          onPointerDown={handleMouseDown}
          onDoubleClick={handleTextDoubleClick}
        >
          <div style={{ position: 'relative' }}>
            <div
              ref={textContentRef}
              contentEditable={isEditingText}
              suppressContentEditableWarning
              onInput={handleTextInput}
              onBlur={handleTextBlur}
              onKeyDown={handleTextKeyDown}
              className="font-normal break-words outline-none whitespace-pre-wrap"
              style={{
                fontSize: `${fontSize}px`,
                lineHeight: textLineSpacing && textLineSpacing > 0 ? textLineSpacing : 1.0,
                fontFamily: fontFamily || 'Arial, sans-serif',
                // Текст рисует canvas-оверлей. Здесь прозрачно + видимая каретка для ввода.
                color: 'transparent',
                caretColor: textColor || '#ffffff',
                textTransform: textUppercase ? 'uppercase' : 'none',
                minHeight: '1.2em',
                textShadow: 'none',
                ...(fontVariationSettings && Object.keys(fontVariationSettings).length > 0
                  ? { fontVariationSettings: buildFontVariationCSS(fontVariationSettings) } as React.CSSProperties
                  : {}),
              }}
            >
              {/* Двухцветный текст: первая строка — основной цвет, остальные — вторичный */}
              {!isEditingText && renderLines.length > 0 ? (
                renderLines.map((line, i) => (
                  <div key={i} style={{ color: 'transparent' }}>
                    {line}
                  </div>
                ))
              ) : (
                text
              )}
            </div>
            {!isEditingText && (
              <>
                <div className="absolute top-0 left-0 w-3 h-3 opacity-0 group-hover:opacity-100 cursor-nw-resize transition-opacity rounded-sm" style={{ background: 'white', margin: '-6px' }} onMouseDown={handleResizeMouseDown} />
                <div className="absolute top-0 right-0 w-3 h-3 opacity-0 group-hover:opacity-100 cursor-ne-resize transition-opacity rounded-sm" style={{ background: 'white', margin: '-6px' }} onMouseDown={handleResizeMouseDown} />
                <div className="absolute bottom-0 left-0 w-3 h-3 opacity-0 group-hover:opacity-100 cursor-sw-resize transition-opacity rounded-sm" style={{ background: 'white', margin: '-6px' }} onMouseDown={handleResizeMouseDown} />
                <div className="absolute bottom-0 right-0 w-3 h-3 opacity-0 group-hover:opacity-100 cursor-se-resize transition-opacity rounded-sm" style={{ background: 'white', margin: '-6px' }} onMouseDown={handleResizeMouseDown} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}